import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import fetchApi from "../../../utils/fetchApi";
import {
  capexURL,
  carbonEmissionProfileURL,
  energyAssetsURL,
  lcoeURL,
  mitigationURL,
  npvURL,
  opexURL,
} from "../api/url";
import {
  ASSET_TYPE_OPTIONS,
  CNP_STEPS,
  CNP_TABS,
  EMISSION_SCOPE_OPTIONS,
  ENERGY_SYSTEM_OPTIONS,
  ENERGYASSETINITIAL,
  EXTERNAL_OPTIONS,
  INPUT_OPTIONS,
  MITIGATION_INITIAL,
  MITIGATION_SCOPES_INITIAL,
  OUTPUT_OPTIONS,
  PATHWAY_OPTIONS,
} from "../constants/cnp.constants";
import {
  _handleEnergyAssetHelper,
  _handleSortEnergyAssets,
  indexedNPVandLCOEData,
} from "../utils/cnp.util";
import { handleFetch } from "../../../utils/handleFetch";
import { getLabel, pick } from "../../../utils/utils";

class CNPStore {
  energyAssets = [];
  npvData = [];
  lcoeData = [];
  mitigation = MITIGATION_INITIAL;
  errors = {};

  cnpSteps = CNP_STEPS;
  cnpTabs = CNP_TABS;
  energySystemOptions = ENERGY_SYSTEM_OPTIONS;
  assetTypeOptions = ASSET_TYPE_OPTIONS;
  inputOptions = INPUT_OPTIONS;
  outputOptions = OUTPUT_OPTIONS;
  externalOptions = EXTERNAL_OPTIONS;
  pathwayOptions = PATHWAY_OPTIONS;
  emissionScopeOptions = EMISSION_SCOPE_OPTIONS;

  overallEnergyProfileError = null;

  currencies = [];
  gwpValues = {
    co2GWP: 1,
    ch4GWP: 27.9,
    n2oGWP: 273,
  };

  isAddNew = false;

  selectedPage = 0;
  selectedTab = 0;
  selectedAsset = null;
  selectedNPVId = null;
  selectedLCOEId = null;

  isAlt = null;
  altAsset = { ...ENERGYASSETINITIAL, reductionTarget: "" };

  constructor() {
    makeAutoObservable(this);
  }

  handleCheckAsset = (_id, value) => {
    const index = this.energyAssets.findIndex((asset) => asset._id === _id);
    if (index > -1) this.energyAssets[index]["isSelected"] = value;
  };

  setMitiCapexSelect = (type, v) => {
    const typeEntry = this.mitigation.types.find((t) => t.type === type);
    if (!typeEntry) return;
    typeEntry["capexSelect"] = v;
  };

  setUnitPrice = (type, v) => {
    const typeEntry = this.mitigation.types.find((t) => t.type === type);
    if (!typeEntry) return;
    typeEntry["unitPrice"] = parseFloat(v);
  };

  setMitigationApplied = (v) => (this.mitigation.mitigationApplied = v);

  setMitigationTypes = (values) => {
    const keys = values.map((v) => v.key);
    this.mitigation.types = keys.map((key) => {
      const find = this.mitigation.types.find((t) => t.type === key);
      if (find) {
        return find;
      } else {
        return {
          type: key,
          scopes: MITIGATION_SCOPES_INITIAL,
        };
      }
    });
  };

  setMitigationScopeData = (entry, scope, e) => {
    const { id, value, type } = e.target;
    const typeEntry = this.mitigation.types.find((t) => t.type === entry);

    if (!typeEntry) return;

    const scopeEntry = typeEntry.scopes.find((si) => si.scope === scope);
    if (scopeEntry) {
      scopeEntry[id] = type === "number" ? parseFloat(value) : value;
    }
  };

  getMitigation = async () => {
    return await handleFetch({
      url: mitigationURL,
      method: "GET",
      onSuccess: (data) => {
        runInAction(() => {
          this.mitigation = { ...MITIGATION_INITIAL, ...data };
        });
      },
    });
  };

  saveMitigation = async () => {
    const { _id, ...body } = this.mitigation;
    return await handleFetch({
      url: mitigationURL,
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data) => {
        runInAction(() => {
          this.mitigation = data;
        });
      },
    });
  };

  setAltAsset = (v) => (this.altAsset = v);
  setIsAddNew = (v) => (this.isAddNew = v);
  setIsAlt = (v) => {
    if (v && !this.isAddNew) {
      let { _id, ...data } = this.selectedAsset;
      data.assetInfo = { ...data.assetInfo, configuration: false };
      this.altAsset = { ...data, baselineId: _id };
    } else {
      this.selectedAsset.hasAlt = false;
    }
    this.isAlt = v;
  };

  setNewAsset = () => (this.selectedAsset = ENERGYASSETINITIAL);

  setSelectedAsset = (id) => {
    const asset = this.energyAssets.find((ass) => ass._id === id) || null;
    if (asset?.hasAlt) {
      const altAsset = this.energyAssets.find(
        (ass) => ass.baselineId === asset._id,
      );
      this.isAlt = true;
      this.altAsset = altAsset;
    } else {
      this.isAlt = false;
      this.altAsset = { ...ENERGYASSETINITIAL, reductionTarget: "" };
    }
    this.selectedAsset = asset;
  };

  handleChangeAssetData = (e, entry, alt) => {
    const { id, type } = e.target;
    const value =
      type === "number" ? parseFloat(e.target.value) : e.target.value;
    const targetAsset = alt ? this.altAsset : this.selectedAsset;
    const altAsset = this.altAsset;

    if (!entry) {
      targetAsset[id] = value;
      altAsset[id] = value;
      return;
    }

    if (entry === "assetInfo") {
      if (id === "input") {
        if (value === "fuel") {
          this.selectedAsset.assetInfo.fuelInfo = {};
        } else if (value === "external") {
          this.selectedAsset.assetInfo.externalInfo = {};
        }
      }

      targetAsset.assetInfo[id] = value;

      if (id === "configuration" && this.isAlt) {
        return;
      }

      altAsset.assetInfo[id] = value;
      return;
    }

    if (entry === "fuelInfo" || entry === "externalInfo") {
      if (!targetAsset.assetInfo[entry]) {
        targetAsset.assetInfo[entry] = {};
      }

      targetAsset.assetInfo[entry][id] = value;

      if (this.selectedAsset.assetInfo.input === altAsset.assetInfo.input) {
        if (!altAsset.assetInfo[entry]) altAsset.assetInfo[entry] = {};
        altAsset.assetInfo[entry][id] = value;
      }
    }
  };

  _handleSelectedAssetId = (v) => (this.selectedAssetId = v);
  _handleSelectedNPVId = (v) => (this.selectedNPVId = v);
  _handleSelectedLCOEId = (v) => (this.selectedLCOEId = v);

  _handlePageAndTab = (type, page, tab, companyId) => {
    const pages = { 0: [0, 1, 2, 3], 1: [0, 1, 2, 3] };
    switch (type) {
      case "next":
        if (this.selectedPage === 1 && this.selectedTab === 3) {
          null;
        }
        if (
          pages[this.selectedPage].length - 1 === this.selectedTab &&
          this.selectedPage !== 2
        ) {
          this.selectedPage++;
          this.selectedTab = 0;
        } else {
          this.selectedTab++;
        }
        break;
      case "previous":
        if (this.selectedTab === 0 && this.selectedPage !== 0) {
          this.selectedPage--;
          this.selectedTab = pages[this.selectedPage].at(-1);
        } else {
          this.selectedTab--;
        }
        break;
      default:
        if (isNaN(page) || isNaN(tab))
          return toast.error("Page and Tab must numbers");
        this.selectedPage = page;
        this.selectedTab = tab;
        break;
    }
    window.history.replaceState(
      null,
      "",
      // `/carbon-neutral-planning/${this.selectedPage}/${this.selectedTab}${companyId && `/${companyId}`}`
      `/carbon-neutral-planning/${this.selectedPage}/${this.selectedTab}${companyId ? `/${companyId}` : ""}`,
    );
  };

  getEnergyAssets = async () => {
    return await handleFetch({
      url: energyAssetsURL(),
      method: "GET",
      onSuccess: (data) => {
        runInAction(() => (this.energyAssets = _handleSortEnergyAssets(data)));
      },
    });
  };

  saveEnergyAsset = async (alt) => {
    const { _id, ...body } = this[alt ? "altAsset" : "selectedAsset"];

    const handleSuccess = (data, message) => {
      runInAction(() => {
        const index = this.energyAssets.findIndex((a) => a._id === _id);
        if (index > -1) this.energyAssets[index] = data;
        else this.energyAssets.push(data);

        if (!alt && !this.isAlt) {
          this.energyAssets = this.energyAssets.filter(
            (ass) => ass.baselineId !== data._id,
          );
        }

        this.energyAssets = _handleSortEnergyAssets(this.energyAssets);

        if (!alt && this.isAlt) {
          this.altAsset.baselineId = data._id;
        }

        if (this.isAddNew) {
          this.selectedAsset = null;
          this.isAddNew = false;
          if (alt) {
            this.isAlt = null;
            this.altAsset = { ...ENERGYASSETINITIAL, reductionTarget: "" };
          }
        }

        this.errors = {};
      });

      if (!alt) {
        toast.success(message);
      }
    };

    return await handleFetch({
      url: energyAssetsURL(_id || false),
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data, message) => handleSuccess(data, message),
      onValidationError: (errors) => {
        runInAction(() => (this.errors = errors));
      },
    });
  };

  deleteEnergyAsset = async (id) => {
    return await handleFetch({
      url: energyAssetsURL(id),
      method: "DELETE",
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.energyAssets.findIndex(
            (asset) => asset._id === data._id,
          );
          this.energyAssets.splice(index, 1);
        });
        toast.success(message);
      },
    });
  };

  saveCarbonEmissionProfile = async () => {
    const body = this.energyAssets.map((asset) => ({
      _id: asset._id,
      value: asset.isSelected,
    }));
    return await handleFetch({
      url: carbonEmissionProfileURL,
      method: "PATCH",
      body,
      onSuccess: (_, message) => {
        runInAction(async () => {
          const success = await this.saveMitigation();
          if (success) {
            toast.success(message);
          }
          return success;
        });
      },
    });
  };

  saveCapex = async (body) => {
    return await handleFetch({
      url: capexURL,
      method: "PATCH",
      body,
      onSuccess: (_, message) => {
        runInAction(async () => {
          const success = await this.saveMitigation();
          if (success) toast.success(message);
          return success;
        });
      },
    });
  };

  saveOpex = async () => {
    const fields = [
      "_id",
      "inputCost",
      "fixedValue",
      "variableValue",
      "othersValue",
      "opexSelect",
    ];

    const body = this.energyAssets.map((asset) => pick(asset, fields));
    return await handleFetch({
      url: opexURL,
      method: "PATCH",
      body,
      onSuccess: (_, message) => toast.success(message),
    });
  };

  getNPV = async () => {
    return await handleFetch({
      url: npvURL(),
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => (this.npvData = indexedNPVandLCOEData(data))),
    });
  };

  getLCOE = async () => {
    return await handleFetch({
      url: lcoeURL(),
      method: "GET",
      onSuccess: (data) =>
        runInAction(() => (this.lcoeData = indexedNPVandLCOEData(data))),
    });
  };

  saveNPV = async (bodyData) => {
    const { _id, ...body } = bodyData;

    return await handleFetch({
      url: npvURL(_id || false),
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.npvData.findIndex((npv) => npv._id === data._id);
          if (index > -1) {
            this.npvData[index] = data;
          } else {
            this.npvData.push(data);
          }
          this.npvData = indexedNPVandLCOEData(this.npvData);
          this.errors = {};
        });
        toast.success(message);
      },
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  saveLCOE = async (bodyData) => {
    const { _id, ...body } = bodyData;

    return await handleFetch({
      url: lcoeURL(_id || false),
      method: _id ? "PUT" : "POST",
      body,
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.lcoeData.findIndex(
            (lcoe) => lcoe._id === data._id,
          );
          if (index > -1) {
            this.lcoeData[index] = data;
          } else {
            this.lcoeData.push(data);
          }
          this.lcoeData = indexedNPVandLCOEData(this.lcoeData);
          this.errors = {};
        });
        toast.success(message);
      },
      onValidationError: (errors) => runInAction(() => (this.errors = errors)),
    });
  };

  deleteNPV = async (id) => {
    return await handleFetch({
      url: npvURL(id),
      method: "DELETE",
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.npvData.findIndex((npv) => npv._id === data._id);
          if (index > -1) {
            this.npvData.splice(index, 1);
          }
        });
        toast.success(message);
      },
    });
  };

  deleteLCOE = async (id) => {
    return await handleFetch({
      url: lcoeURL(id),
      method: "DELETE",
      onSuccess: (data, message) => {
        runInAction(() => {
          const index = this.lcoeData.findIndex(
            (lcoe) => lcoe._id === data._id,
          );
          if (index > -1) {
            this.lcoeData.splice(index, 1);
          }
        });
        toast.success(message);
      },
    });
  };

  _getNodeAndEdgeDataOfOverallEnergy = () => {
    const getNodeLabel = (value) => {
      const energySystemLabel = {
        Electricity: "⚡ Electricity Load",
        Heat: "🔥 Heating Load",
        Cooling: "❄️ Cooling Load",
      };

      // if (value === 'external') return 'External Energy Resource';
      if (value === "external") return;

      return energySystemLabel[value] ?? value;
    };

    const getEdgeLabel = (assetName, energySystem) => {
      const labelPostfix = assetName
        .split(" ")
        .map((item) => item.charAt(0))
        .join("");
      const labelPrefix =
        energySystem === "Electricity"
          ? "P"
          : energySystem.charAt(0).toUpperCase();

      return `${labelPrefix}_${labelPostfix}`;
    };

    const [nodeData, edgeData, nodeList] = [[], [], new Set()];
    const energySystems = this.energyAssets.filter((asset) => asset.isSelected);

    this.overallEnergyProfileError = null;

    energySystems.forEach((asset) => {
      const { energySystem, assetType, index, input, output, baselineId } =
        _handleEnergyAssetHelper(asset);
      const assetName = `${assetType}_${index}${baselineId ? "*" : ""}`;

      if (!output || !input) {
        this.overallEnergyProfileError = { message: "", assets: [] };
        this.overallEnergyProfileError.message =
          "Please fill input, output informations completely in following assets";
        this.overallEnergyProfileError.assets.push(assetName);
      }

      const [assetNode, inputNode, outputNode] = [
        getNodeLabel(assetName),
        getNodeLabel(asset.assetInfo.input),
        getNodeLabel(output),
      ];

      if (!nodeList.has(inputNode))
        nodeData.push({
          data: getLabel(this.inputOptions, inputNode),
        });
      if (!nodeList.has(assetNode))
        nodeData.push({ data: assetNode, nodeType: energySystem });
      if (!nodeList.has(outputNode))
        nodeData.push({
          data: outputNode,
          nodeType: output,
        });

      edgeData.push({
        source: getLabel(this.inputOptions, inputNode),
        target: assetNode,
      });
      edgeData.push({
        source: assetNode,
        target: outputNode,
        label: getEdgeLabel(assetType, energySystem),
        edgeType: energySystem,
      });

      if (
        asset.assetInfo?.externalInfo &&
        asset.assetInfo?.input === "external"
      ) {
        const { resource, resourceName } = asset.assetInfo.externalInfo;
        const externalNode =
          resource === "other"
            ? resourceName
            : this.externalOptions.find((e) => e.key === resource)?.value;

        if (!nodeList.has(externalNode))
          nodeData.push({ data: externalNode, nodeType: "external" });
        edgeData.push({
          source: externalNode,
          target: assetNode,
          edgeType: "external",
        });
      }
    });
    return { nodeData, edgeData };
  };
}

export const cnpStore = new CNPStore();
